import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function FAQPage() {
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const isRTL = i18n.language === 'fa';

  const faqs = [
    {
      question: t('faq.q1'),
      answer: t('faq.a1')
    },
    {
      question: t('faq.q2'),
      answer: t('faq.a2')
    },
    {
      question: t('faq.q3'),
      answer: t('faq.a3')
    },
    {
      question: t('faq.q4'),
      answer: t('faq.a4')
    },
    {
      question: t('faq.q5'),
      answer: t('faq.a5')
    },
    {
      question: t('faq.q6'),
      answer: t('faq.a6')
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/faq#webpage`,
        url: `${siteUrl}/faq`,
        name: 'Frequently Asked Questions - Loveish Group',
        description: 'Find answers to common questions about our products, shipping, returns, and more.',
        isPartOf: {
          '@id': `${siteUrl}/#website`
        },
        breadcrumb: {
          '@id': `${siteUrl}/faq#breadcrumb`
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/faq#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'FAQ',
            item: `${siteUrl}/faq`
          }
        ]
      },
      {
        '@type': 'FAQPage',
        '@id': `${siteUrl}/faq#faqpage`,
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }
    ]
  };

  useSEO({
    title: 'Frequently Asked Questions - Loveish Group',
    description: 'Find answers to common questions about our products, shipping, returns, and more.',
    keywords: 'FAQ, frequently asked questions, help, support',
    canonical: `${siteUrl}/faq`,
    schema: faqSchema
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20 md:pt-24">
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Frequently Asked Questions
            </h1>
            <p className="text-base md:text-lg text-gray-600 text-center">
              Find answers to common questions about our products and services
            </p>
          </div>
        </div>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className={`w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}
                  aria-expanded={openIndex === index}
                >
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h2>
                  <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-emerald-600 flex-shrink-0`}></i>
                </button>
                
                {openIndex === index && (
                  <div className="px-6 pb-5 animate-slide-up">
                    <p className={`text-base md:text-lg text-gray-600 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>

          <div className="mt-12 md:mt-16 bg-emerald-50 rounded-2xl p-8 md:p-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Still have questions?
            </h2>
            <p className="text-base md:text-lg text-gray-600 mb-6">
              Our customer support team is here to help you
            </p>
            <a
              href="/contact"
              className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Us
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}